import React, { useState } from 'react';
import {
  Box,
  LinearProgress,
  Typography,
  alpha,
  useTheme,
  IconButton,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { ClientField } from 'shared/types/appTypes';

interface FormProgressProps {
  current: number;
  total: number;
  fields?: ClientField[];
  getFieldValue?: (fieldIndex: number) => any;
  onNavigate?: (index: number) => void;
}

const hasValue = (value: any): boolean => {
  if (value == null) return false;
  if (typeof value === 'string' && value.trim() === '') return false;
  if (Array.isArray(value)) {
    return value.some((item) => String(item || '').trim().length > 0);
  }
  if (typeof value === 'object') {
    // Address objects
    if (
      'addressLine1' in value ||
      'addressLine2' in value ||
      'city' in value ||
      'state' in value ||
      'postalCode' in value ||
      'country' in value
    ) {
      return [
        value.addressLine1,
        value.addressLine2,
        value.city,
        value.state,
        value.postalCode,
        value.country,
      ].some((part) => !!String(part || '').trim());
    }
    // For date/time ranges
    if ('start' in value || 'end' in value) {
      return value.start != null || value.end != null;
    }
  }
  return true;
};

const FormProgress: React.FC<FormProgressProps> = ({
  current,
  total,
  fields,
  getFieldValue,
  onNavigate,
}) => {
  const theme = useTheme();
  const progress = (current / total) * 100;
  const [expanded, setExpanded] = useState(false);

  const showToc = fields && getFieldValue && onNavigate;

  const handleToggle = () => {
    setExpanded((prev) => !prev);
  };

  const handleNavigate = (index: number) => {
    setExpanded(false);
    onNavigate?.(index);
  };

  return (
    <Box sx={{ flexShrink: 0 }}>
      {/* TOC List - expands above the progress bar */}
      {showToc && (
        <Collapse in={expanded}>
          <Box
            sx={{
              maxHeight: '40vh',
              overflowY: 'auto',
              borderTop: `1px solid ${theme.palette.divider}`,
              bgcolor: alpha(theme.palette.background.paper, 0.95),
            }}
          >
            <List dense disablePadding>
              {fields.map((field, index) => {
                const fieldHasValue = hasValue(getFieldValue(index));
                const isCurrent = index === current - 1;
                const isVideoField = field.type === 'video';
                return (
                  <ListItemButton
                    key={index}
                    onClick={() => handleNavigate(index)}
                    selected={isCurrent}
                    sx={{
                      py: 1,
                      '&.Mui-selected': {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                      },
                    }}
                  >
                    {isVideoField ? (
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <PlayCircleOutlineIcon
                          sx={{ fontSize: 18, color: 'primary.main' }}
                        />
                      </ListItemIcon>
                    ) : (
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {fieldHasValue ? (
                          <CheckCircleIcon
                            sx={{ fontSize: 18, color: 'success.main' }}
                          />
                        ) : (
                          <RadioButtonUncheckedIcon
                            sx={{ fontSize: 18, color: 'text.disabled' }}
                          />
                        )}
                      </ListItemIcon>
                    )}
                    <ListItemText
                      primary={field.label}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: isCurrent ? 600 : 400,
                        noWrap: true,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.disabled', ml: 1 }}
                    >
                      {index + 1}
                    </Typography>
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        </Collapse>
      )}

      {/* Progress bar row */}
      <Box
        sx={{
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            flex: 1,
            height: 4,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            '& .MuiLinearProgress-bar': {
              borderRadius: 2,
              transition: 'transform 0.4s ease-in-out',
            },
          }}
        />
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontWeight: 500,
            fontSize: '0.875rem',
          }}
        >
          {current} / {total}
        </Typography>
        {showToc && (
          <IconButton
            size="small"
            onClick={handleToggle}
            sx={{
              color: 'text.secondary',
              transition: 'transform 0.2s',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            {expanded ? (
              <KeyboardArrowDownIcon fontSize="small" />
            ) : (
              <FormatListBulletedIcon fontSize="small" />
            )}
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

export default FormProgress;
